/**
 * @description Renders a filepond field for retrieving the personnel file to create the map from. When the file is uploaded,
 *  the UI will send a request to the backend to read its contents and parse the column headers.  Those headers are then
 *  transformed into a map of column-header/field-id pairs and saved to the form state so it can be used to populate the rest
 *  of the form.
 */

import React from 'react';
import { PropTypes } from 'prop-types';
import { useFormContext, Controller } from 'react-hook-form';
import { FilePond } from 'react-filepond';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';

import CustomFormLabel from '@seccon/shared/ui/components/CustomFormLabel';

import useFilePondEventProps from '../hooks/useFilePondEventProps';

/**
 * @prop setHeaderMap {function} - sets the header map containing a pairing of form ids to personnel column headers
 */
const MappingFile = ({
  setHeaderMap
}) => {
  const { control, setError, clearErrors } = useFormContext();

  return (
    <Controller
      name='mappingFile'
      control={control}
      defaultValue={[]}
      render={({ field: { onChange, value }, fieldState }) => {
        const { error } = fieldState;

        // This hook provides the event properties used to integrate with the filepond interface and retrieve/process the
        // personnel file.
        const eventProps = useFilePondEventProps({
          clearErrors,
          error,
          onChange,
          setError,
          setHeaderMap,
          value
        });

        return (
          <FormControl fullWidth error={Boolean(error)} required>
            <CustomFormLabel label='Personnel File to Map' p={0}>
              <FilePond
                acceptedFileTypes={[
                  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                ]}
                allowMultiple={false}
                credits={false}
                files={value}
                fileValidateTypeDetectType={(source, type) => {
                  // Any file with a xlsx extension should be normalized to the spreadsheet mime type
                  if (/.xlsx$/.test(source.name)) {
                    return Promise.resolve('application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
                  }

                  return Promise.resolve(type);
                }}
                fileValidateTypeLabelExpectedTypes='Expects .xlsx'
                labelIdle='Drag & Drop the File or <span class="filepond--label-action">Browse</span>'
                onerror={(err) => setError('mappingFile', { type: 'custom', message: Object.values(err).join('. ') })}
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...eventProps}
              />
            </CustomFormLabel>
            {error && (
              <FormHelperText id='syncFile-text'>
                {error.message}
              </FormHelperText>
            )}
          </FormControl>
        );
      }}
      rules={{
        required: 'Personnel File is required'
      }}
    />
  );
};

MappingFile.propTypes = {
  setHeaderMap: PropTypes.func.isRequired
};

export default MappingFile;
